/* global Stripe */
import React, {Component} from 'react';
import loadScript from 'load-script';
import {injectIntl} from 'react-intl';
import TrialEndCreateCustomerMutation from '../../mutations/trial_end_create_customer_mutation';
import ChangeStripeDataMutation from '../../mutations/change_stripe_data_mutation';
import UploadingOverlay from '../../forecast-app/shared/components/uploading-overlay/uploading_overlay';
import Util from '../../forecast-app/shared/util/util';
import {BUTTON_STYLE, BUTTON_COLOR} from '../../constants';
import Button from '../../forecast-app/shared/components/buttons/button/button';
import UpdateCompanyMutation from '../../mutations/update_company_mutation.modern';
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';
import InputField from '../new-ui/input_field';
import * as tracking from '../../tracking';

let scriptLoadingStarted = false;

const useSCA = true;

class Payment extends Component {
	constructor(props) {
		super(props);

		this.onApiLoad = this.onApiLoad.bind(this);

		this.state = {
			loading: false,
			apiLoaded: false,
			editCardEnabled: false,
			showEmailValidationError: false,
			emailValidationErrorMessage: '',
		};
	}

	componentDidMount() {
		tracking.trackPage('trial-end-payment');
		if (this.isStripeReady()) {
			// init immediately
			this.onApiLoad();
		} else if (!scriptLoadingStarted) {
			// load google api and the init
			scriptLoadingStarted = true;
			loadScript('https://js.stripe.com/v3/', this.onApiLoad);
		} else {
			// is loading
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.editCardEnabled !== this.state.editCardEnabled && this.state.editCardEnabled) {
			if (this.state.editCardEnabled) {
				if (this.isStripeReady()) {
					// init immediately
					this.onApiLoad();
				} else if (!scriptLoadingStarted) {
					// load google api and the init
					scriptLoadingStarted = true;
					loadScript('https://js.stripe.com/v3/', this.onApiLoad);
				} else {
					// is loading
				}
			}
		}
	}

	onApiLoad() {
		// Create a Stripe client
		this.stripe = Stripe(process.env.STRIPE_PUBLIC_KEY);

		const elementStyles = {
			base: {
				fontSize: '12px',
				fontFamily: 'neue-haas-grotesk-text,sans-serif',
				fontSmoothing: 'antialiased',
				color: 'rgba(0, 0, 0, 0.87)',
				fontWeight: '400',

				'::placeholder': {
					fontSize: '12px',
					color: 'rgba(0, 0, 0, 0.50)',
					fontWeight: '400',
				},
			},
		};

		const elements = this.stripe.elements({
			fonts: [
				{
					cssSrc: 'https://use.typekit.net/sjr0oum.css',
				},
			],
		});

		if ((this.props.changeCreditCard && this.state.editCardEnabled) || !this.props.changeCreditCard) {
			this.card = elements.create('cardNumber', {
				style: elementStyles,
			});
			this.card.mount('#card-element');
			const cardExpiry = elements.create('cardExpiry', {
				style: elementStyles,
			});
			cardExpiry.mount('#card-exp');

			const cardCvc = elements.create('cardCvc', {
				style: elementStyles,
			});
			cardCvc.mount('#card-cvc');

			// Handle real-time validation errors from the card Element.
			this.card.addEventListener('change', function (event) {
				const displayError = document.getElementById('card-errors');
				if (event.error) {
					displayError.textContent = event.error.message;
				} else {
					displayError.textContent = '';
				}
			});
			this.setState({apiLoaded: true});
		}
	}

	isStripeReady() {
		return !!window.Stripe;
	}

	handleFieldChange(property, e) {
		this.props.handleFieldChange(property, e);
	}

	selectBilling(val) {
		if (val === 'month') {
			this.selectPaymentType('card');
		}

		this.props.setBilling(val);
	}

	selectPaymentType(val) {
		this.props.setPaymentType(val);
	}

	validateInvoice() {
		this.props.validateInvoice();
	}

	setPage(page, focusZip = false, focusVat = false) {
		if (focusZip) {
			this.props.focusZip();
		} else if (focusVat) {
			this.props.focusVat();
		} else {
			this.props.setPage(page, focusZip);
		}
	}

	toggleEdit(e) {
		this.props.toggleEdit(e);
	}

	processUpgradeSuccess(result) {
		let error = null;

		if (result.trialEndCreateCustomer && result.trialEndCreateCustomer.errors) {
			error = result.trialEndCreateCustomer.errors[0];
		} else if (result.changeStripeData && result.changeStripeData.errors) {
			error = result.changeStripeData.errors[0];
		}

		if (error) {
			const {formatMessage} = this.props.intl;
			const displayError = document.getElementById('card-errors');
			const errorString = '' + error;
			if (!this.props.changeInvoiceInformation && !this.props.changeCreditCard) {
				if (error === 'ZIP_CODE_INVALID' || error === 'billing_address[zip] : invalid zip/postal code') {
					this.setPage('COMPANY_DETAILS', true, false);
					return;
				}
				if (error === 'VAT_NUMBER') {
					this.setPage('COMPANY_DETAILS', false, true);
					return;
				}
			}

			// TODO: Handle representation of Chargebee errors better.
			if (errorString.toLowerCase().includes('chargebee')) {
				if (errorString.toLowerCase().includes('email')) {
					displayError.textContent = formatMessage({id: 'signup.email-is-invalid'});
				} else {
					let errorMessage = errorString.split('Error message : ');
					if (errorMessage.length > 1) {
						displayError.textContent = errorMessage[1];
					} else {
						displayError.textContent = formatMessage({id: 'stripe_error.missing'});
					}
				}
			} else {
				if (error === 'customer[email] : wrong format') {
					displayError.textContent = this.props.intl.formatMessage({id: 'signup.email-is-invalid'});
				} else {
					displayError.textContent = formatMessage({id: 'stripe_error.' + error});
				}
			}
			this.setState({loading: false});
		} else {
			!this.props.changeCreditCard && !this.props.changeInvoiceInformation
				? this.setPage('COMPLETE')
				: this.toggleEdit('SUCCESS');
		}
	}

	updateCompanyInformation(result) {
		let error = null;
		const mutationObject = {id: this.props.viewer.company.id};

		if (result.changeStripeData && result.changeStripeData.errors) {
			error = result.changeStripeData.errors[0];
		}

		if (error) {
			const {formatMessage} = this.props.intl;
			const displayError = document.getElementById('card-errors');
			const errorString = '' + error;

			// TODO: Handle representation of Chargebee errors better.
			if (errorString.toLowerCase().includes('chargebee')) {
				if (errorString.toLowerCase().includes('email')) {
					displayError.textContent = formatMessage({id: 'signup.email-is-invalid'});
				} else {
					let errorMessage = errorString.split('Error message : ');
					displayError.textContent = errorMessage[1];
				}
			} else {
				if (error === 'customer[email] : wrong format') {
					displayError.textContent = this.props.intl.formatMessage({id: 'signup.email-is-invalid'});
				} else {
					displayError.textContent = formatMessage({id: 'stripe_error.' + error});
				}
			}
			this.setState({loading: false});
		} else {
			if (this.props.contactPhone && this.props.contactPhone !== this.props.viewer.company.contactPhone) {
				mutationObject.contactPhone = this.props.contactPhone;
			}
			if (this.props.contactName && this.props.contactName !== this.props.viewer.company.contactName) {
				mutationObject.contactName = this.props.contactName;
			}
			Util.CommitMutation(UpdateCompanyMutation, mutationObject, this.processUpgradeSuccess.bind(this));
		}
	}

	// Used when either signing up with invoice or changing invoice information through the subscription page
	processInvoiceUpgrade() {
		const validTemp =
			this.props.contactName && this.props.invoiceEmail !== '' && this.props.invoiceEmail && this.props.contactPhone;
		const valid = validTemp !== null && validTemp !== '' ? validTemp : false;

		if (this.state.discount_error) {
			const field = document.getElementById('discount_code');
			field.focus();
			return;
		}
		if (!valid) {
			if (!this.props.contactName) {
				const field = document.getElementById('company_contact_name');
				field.focus();
				return;
			} else if (!this.props.contactPhone) {
				const field = document.getElementById('company_contact_phone');
				field.focus();
				return;
			} else if (!this.props.invoiceEmail || this.props.invoiceEmail === '') {
				const field = document.getElementById('invoice_email');
				field.focus();
				return;
			}
		}

		this.setState({loading: true});

		if (!this.props.changeInvoiceInformation) {
			Util.CommitMutation(
				TrialEndCreateCustomerMutation,
				{
					billingPeriod: this.props.billing.toUpperCase(),
					// Contact email for invoicing
					invoiceEmail: this.props.invoiceEmail,
					contactPhone: this.props.contactPhone,
					coupon: this.props.appliedCoupon ? this.props.appliedCoupon.couponId : null,
					seats: +this.props.seats,
					virtualSeats: +this.props.virtualSeats,
					withOnboarding: this.props.isOnboarding,
					planId: this.props.selectedPlan.chargebeeId,
					virtualUserId: this.props.selectedVirtualUser.chargebeeId,
					onboardingId: this.props.selectedOnboarding.chargebeeId,
				},
				this.processUpgradeSuccess.bind(this)
			);
		} else {
			const mutationObject = {isChargebee: true};
			if (this.props.invoiceEmail) {
				mutationObject.invoiceEmail = this.props.invoiceEmail;
			}
			Util.CommitMutation(ChangeStripeDataMutation, mutationObject, this.updateCompanyInformation.bind(this));
		}
	}

	checkEmailValidity(isValid) {
		this.setState({
			showEmailValidationError: !isValid,
			emailValidationErrorMessage: !isValid
				? this.props.intl.formatMessage({id: 'settings_team.invalid-email'})
				: this.props.intl.formatMessage({id: 'common.field-cannot-be-empty'}),
		});
	}

	// Used when either signing up with card or changing card information through the subscription page
	processUpgrade() {
		const validTemp =
			(this.props.cardName && this.props.invoiceEmail !== '' && this.props.invoiceEmail) ||
			(this.props.changeCreditCard && this.props.cardName);
		const valid =
			validTemp !== null && validTemp !== '' && this.props.invoiceEmail && this.props.invoiceEmail === ''
				? validTemp
				: false;

		if (!valid) {
			if (!this.props.invoiceEmail || this.props.invoiceEmail === '') {
				const field = document.getElementById('invoice_email');
				field.focus();
				return;
			} else if (!this.props.cardName) {
				const field = document.getElementById('credit_card_name_on_card');
				field.focus();
				return;
			}
		}

		this.setState({loading: true});

		let extraDetails;

		if (!this.props.changeCreditCard) {
			if (useSCA) {
				extraDetails = {
					billing_details: {
						name: this.props.cardName,
						address: {
							line1: this.props.viewer.company.street,
							city: this.props.viewer.company.city,
							postal_code: this.props.viewer.company.zip,
							country: this.props.viewer.company.country,
						},
					},
				};
			} else {
				extraDetails = {
					name: this.props.cardName,
					address_line1: this.props.viewer.company.street,
					address_city: this.props.viewer.company.city,
					address_zip: this.props.viewer.company.zip,
					address_country: this.props.viewer.company.country,
				};
			}
		} else {
			if (useSCA) {
				extraDetails = {
					billing_details: {
						name: this.props.cardName,
					},
				};
			} else {
				extraDetails = {
					name: this.props.cardName,
				};
			}
		}

		const onSuccess = result => {
			if (!this.state.showEmailValidationError) {
				createToast({
					duration: 5000,
					message: this.props.intl.formatMessage({id: 'common.updated'}),
				});
				this.setState({loading: false});
			}
			this.setState({loading: false}, () => this.toggleEdit('SUCCESS'));
		};

		if (this.props.changeCreditCard && !this.state.editCardEnabled) {
			Util.CommitMutation(
				ChangeStripeDataMutation,
				{
					invoiceEmail: this.props.invoiceEmail,
				},
				onSuccess
			);
		} else {
			if (useSCA) {
				this.stripe.createPaymentMethod('card', this.card, extraDetails).then(result => {
					if (result.error) {
						this.setState({loading: false});
						return;
					}
					if (!this.props.changeCreditCard) {
						// New trial end/payment
						fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'stripe_3ds_confirm', {
							credentials: 'include',
							method: 'POST',
							headers: {'Content-Type': 'application/json'},
							body: JSON.stringify({
								payment_method_id: result.paymentMethod.id,
								amount: this.props.getTotalWithVat(),
							}),
						}).then(result => {
							// Handle server response
							result.json().then(json => {
								this.handleServerResponse(json);
							});
						});
					} else {
						// Change credit card
						fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'stripe_3ds_confirm', {
							credentials: 'include',
							method: 'POST',
							headers: {'Content-Type': 'application/json'},
							body: JSON.stringify({
								payment_method_id: result.paymentMethod.id,
							}),
						}).then(result => {
							// Handle server response
							result.json().then(json => {
								this.handleServerResponse(json);
							});
						});
					}
				});
			} else {
				this.stripe.createToken(this.card, extraDetails).then(result => {
					if (result.token) {
						if (!this.props.changeCreditCard) {
							Util.CommitMutation(
								TrialEndCreateCustomerMutation,
								{
									token: result.token.id,
									billingPeriod: this.props.billing.toUpperCase(),
									invoiceEmail: this.props.invoiceEmail,
									coupon: this.props.appliedCoupon ? this.props.appliedCoupon.couponId : null,
									seats: +this.props.seats,
									virtualSeats: +this.props.virtualSeats,
									withOnboarding: this.props.isOnboarding,
									planId: this.props.selectedPlan.chargebeeId,
									virtualUserId: this.props.selectedVirtualUser.chargebeeId,
									onboardingId: this.props.selectedOnboarding.chargebeeId,
								},
								this.processUpgradeSuccess.bind(this)
							);
						} else {
							Util.CommitMutation(
								ChangeStripeDataMutation,
								{
									invoiceEmail: this.props.invoiceEmail,
									cardholderName: this.props.cardName,
									token: result.token.id,
								},
								this.processUpgradeSuccess.bind(this)
							);
						}
					} else if (result.error) {
						const displayError = document.getElementById('card-errors');
						if (result.error) {
							displayError.textContent = result.error.message;
						} else {
							displayError.textContent = '';
						}
						this.setState({loading: false});
					}
				});
			}
		}
	}

	handleServerResponse(response) {
		if (response.error) {
			// Show error from server on payment form
			// Show error in payment form
			const displayError = document.getElementById('card-errors');
			displayError.textContent = response.error.message;
			this.setState({loading: false});
		} else if (response.status === 400) {
			const displayError = document.getElementById('card-errors');
			displayError.textContent =
				response.message != null ? response.message : 'An error occurred. Please try again or try another card';
			this.setState({loading: false});
		} else if (response.requires_action) {
			// Use Stripe.js to handle required card action
			this.handleAction(response);
		} else {
			if (!this.props.changeCreditCard) {
				Util.CommitMutation(
					TrialEndCreateCustomerMutation,
					{
						token: response.payment_intent_id,
						billingPeriod: this.props.billing.toUpperCase(),
						invoiceEmail: this.props.invoiceEmail,
						coupon: this.props.appliedCoupon ? this.props.appliedCoupon.couponId : null,
						seats: +this.props.seats,
						virtualSeats: +this.props.virtualSeats,
						withOnboarding: this.props.isOnboarding,
						planId: this.props.selectedPlan.chargebeeId,
						virtualUserId: this.props.selectedVirtualUser.chargebeeId,
						onboardingId: this.props.selectedOnboarding.chargebeeId,
					},
					this.processUpgradeSuccess.bind(this)
				);
			} else {
				Util.CommitMutation(
					ChangeStripeDataMutation,
					{
						invoiceEmail: this.props.invoiceEmail,
						token: response.payment_intent_id,
					},
					this.processUpgradeSuccess.bind(this)
				);
			}
		}
	}

	handleAction(response) {
		this.stripe.handleCardAction(response.payment_intent_client_secret).then(result => {
			if (result.error) {
				// Show error in payment form
				const displayError = document.getElementById('card-errors');

				displayError.textContent = result.error.message;
				this.setState({loading: false});
			} else {
				// The card action has been handled
				// The PaymentIntent can be confirmed again on the server
				fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'stripe_3ds_confirm', {
					credentials: 'include',
					method: 'POST',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify({
						payment_intent_id: result.paymentIntent ? result.paymentIntent.id : result.setupIntent.id,
					}),
				})
					.then(response => response.json())
					.then(confirmResult => {
						this.handleServerResponse(confirmResult);
					});
			}
		});
	}

	toggleEditCard() {
		this.setState(prevState => {
			return {editCardEnabled: !prevState.editCardEnabled};
		});
	}

	render() {
		const {formatMessage} = this.props.intl;
		const editCard = (this.props.changeCreditCard && this.state.editCardEnabled) || !this.props.changeCreditCard;
		return (
			<div className="trial-end-card large no-padding">
				<div className="payment-v2">
					<h3 className="header">
						{!this.props.changeCreditCard && !this.props.changeInvoiceInformation
							? formatMessage({id: 'settings_subscription.payment-details'})
							: formatMessage({id: 'settings_subscription.edit_billing_info'})}
					</h3>
					{this.state.loading ? <UploadingOverlay /> : null}
					{!this.props.changeCreditCard && !this.props.changeInvoiceInformation ? (
						<div className="payment-type-radio-wrapper">
							<div className="payment-type-radio">
								<div className="radio-wrapper">
									<input
										type="radio"
										id="invoice-radio"
										name="payment-type"
										onChange={this.selectPaymentType.bind(this, 'invoice')}
										checked={this.props.paymentType === 'invoice'}
										tabIndex={0}
									/>
									<label htmlFor="invoice-radio" data-cy={'invoice-radio'}>
										{formatMessage({id: 'settings_subscription.invoice'})}
									</label>
								</div>
							</div>
							<div className="payment-type-radio no-top-border">
								<div className="radio-wrapper">
									<input
										type="radio"
										id="card-radio"
										name="payment-type"
										onChange={this.selectPaymentType.bind(this, 'card')}
										checked={this.props.paymentType === 'card'}
									/>
									<label htmlFor="card-radio" data-cy={'card-radio'}>
										{formatMessage({id: 'settings_subscription.credit_card'})}
									</label>
								</div>
							</div>
						</div>
					) : null}

					<div className="content">
						{this.props.paymentType === 'card' ? (
							<div>
								<label htmlFor="invoice_email">
									<span className="required">
										{formatMessage({id: 'settings_subscription.invoice-email'}).toUpperCase()}
									</span>
									<InputField
										id="invoice_email"
										value={this.props.invoiceEmail}
										onChange={this.handleFieldChange.bind(this, 'invoiceEmail')}
										type="email"
										placeholder={formatMessage({id: 'settings_subscription.invoice-email'})}
										hideLabel={true}
										checkEmailValidity={this.checkEmailValidity.bind(this)}
										invalidInput={this.state.showEmailValidationError}
										errorMessage={this.state.emailValidationErrorMessage}
										shouldFocus={this.state.showEmailValidationError}
										cy={'invoice-email-input'}
									/>
								</label>
							</div>
						) : (
							<div>
								<label htmlFor="company_contact_name">
									<span className="required">
										{formatMessage({id: 'company_details.contact_name_label'}).toUpperCase()}
									</span>
									<input
										type="text"
										placeholder={formatMessage({id: 'company_details.contact_name_placeholder'})}
										id="company_contact_name"
										size="70"
										value={this.props.contactName}
										onChange={this.handleFieldChange.bind(this, 'contactName')}
										data-cy={'contact-name-input'}
									/>
								</label>
								<label htmlFor="company_contact_phone">
									<span className="required">
										{formatMessage({id: 'company_details.contact_phone_label'}).toUpperCase()}
									</span>
									<input
										type="text"
										placeholder={formatMessage({id: 'company_details.contact_phone_placeholder'})}
										id="company_contact_phone"
										size="70"
										value={this.props.contactPhone}
										onChange={this.handleFieldChange.bind(this, 'contactPhone')}
										data-cy={'contact-phone-input'}
									/>
								</label>
								<label htmlFor="invoice_email">
									<span className="required">
										{formatMessage({id: 'settings_subscription.invoice-email'}).toUpperCase()}
									</span>
									<input
										type="text"
										placeholder={formatMessage({id: 'settings_subscription.invoice-email'})}
										id="invoice_email"
										size="70"
										value={this.props.invoiceEmail}
										onChange={this.handleFieldChange.bind(this, 'invoiceEmail')}
										data-cy={'invoice-email-input'}
									/>
								</label>
								<div id="card-errors" data-cy={'card-errors'} />
							</div>
						)}
						{this.props.changeCreditCard ? (
							<Button
								className="edit-card"
								buttonStyle={BUTTON_STYLE.FILLED}
								colorTheme={BUTTON_COLOR.PURPLE}
								text={
									this.state.editCardEnabled
										? formatMessage({id: 'common.cancel'})
										: formatMessage({id: 'settings_subscription.edit_credit_card'})
								}
								onClick={this.toggleEditCard.bind(this)}
								cy={'edit-card-button'}
							/>
						) : null}

						{/* Ugly visibility hack, but it is required since stripe field will not load if it is conditionally rendered. */}
						<label
							htmlFor="credit_card_name"
							className={'stripe-card-field' + (this.props.paymentType === 'invoice' ? ' invisible' : '')}
						>
							<span className={editCard ? 'required' : ''}>
								{formatMessage({id: 'settings_subscription.card_number'}).toUpperCase()}
							</span>
							{editCard ? (
								<div id="card-element" className="card-input">
									{/* a Stripe Element will be inserted here. */}
								</div>
							) : (
								<input
									disabled={true}
									className="card-input disabled"
									placeholder={'**** **** **** ' + this.props.cardLast4}
								/>
							)}

							{/* Used to display form errors */}
							<div id="card-errors" data-cy={'card-errors'} />
						</label>
						{/* Ugly visibility hack, but it is required since stripe field will not load if it is conditionally rendered. */}
						<div className={'name-exp-cvc-wrapper' + (this.props.paymentType === 'invoice' ? ' invisible' : '')}>
							<label className="stripe-card-name" htmlFor="credit_card_name_on_card">
								<span className={editCard ? 'required' : ''}>
									{formatMessage({id: 'settings_subscription.card_name'}).toUpperCase()}
								</span>
								<input
									className={!editCard ? 'disabled' : ''}
									type="text"
									placeholder={formatMessage({id: 'settings_subscription.name-on-card'})}
									id="credit_card_name_on_card"
									size="70"
									value={this.props.cardName}
									onChange={this.handleFieldChange.bind(this, 'cardName')}
									disabled={!editCard}
									data-cy={'name-on-card-input'}
								/>
							</label>
							<label
								htmlFor="card-exp"
								className={'stripe-exp-field' + (this.props.paymentType === 'invoice' ? ' invisible' : '')}
							>
								<span className={editCard ? 'required' : ''}>
									{formatMessage({id: 'settings_subscription.expiration'}).toUpperCase()}
								</span>
								{editCard ? (
									<div id="card-exp" className="card-input">
										{/* a Stripe Element will be inserted here. */}
									</div>
								) : (
									<input disabled={true} className="card-input disabled" placeholder={'MM / YY'} />
								)}
								{/* Used to display form errors */}
								<div id="card-errors2" />
							</label>
							<label
								htmlFor="card-cvc"
								className={'stripe-cvc-field' + (this.props.paymentType === 'invoice' ? ' invisible' : '')}
							>
								<span className={editCard ? 'required' : ''}>{formatMessage({id: 'CVC'}).toUpperCase()}</span>

								{editCard ? (
									<div id="card-cvc" className="card-input">
										{/* a Stripe Element will be inserted here. */}
									</div>
								) : (
									<input disabled={true} className="card-input disabled" placeholder={'***'} />
								)}
								{/* Used to display form errors */}
								<div id="card-errors3" />
							</label>
						</div>
					</div>
					<div className="buttons">
						{this.props.paymentType === 'card' ? (
							<a href="http://stripe.com" target="_blank" className="stripe-logo" rel="noopener noreferrer" />
						) : null}
						<Button
							className="payment-back-button"
							onClick={
								!this.props.changeCreditCard && !this.props.changeInvoiceInformation
									? this.setPage.bind(this, 'COMPANY_DETAILS', false, false)
									: this.toggleEdit.bind(this)
							}
							buttonStyle={BUTTON_STYLE.FILLED}
							colorTheme={BUTTON_COLOR.WHITE}
							text={
								!this.props.changeCreditCard && !this.props.changeInvoiceInformation
									? formatMessage({id: 'common.back'})
									: formatMessage({id: 'common.cancel'})
							}
							cy={'back-button'}
						/>
						<Button
							className="next-button"
							isDisabled={
								(this.state.editCardEnabled && (this.state.loading || !this.state.apiLoaded)) ||
								this.state.showEmailValidationError
							}
							buttonTitle={
								this.state.editCardEnabled && (this.state.loading || !this.state.apiLoaded)
									? formatMessage({id: 'common.loading'})
									: this.props.billing !== 'annual' && this.props.paymentType === 'invoice'
									? formatMessage({id: 'settings_subscription.invoice_annual_only'})
									: ''
							}
							onClick={
								this.props.paymentType === 'card'
									? this.processUpgrade.bind(this)
									: this.processInvoiceUpgrade.bind(this)
							}
							buttonStyle={BUTTON_STYLE.FILLED}
							colorTheme={BUTTON_COLOR.GREEN}
							text={
								!this.props.changeCreditCard && !this.props.changeInvoiceInformation
									? formatMessage({id: 'settings_subscription.pay'})
									: formatMessage({id: 'common.save'})
							}
							cy={'next-button'}
						/>
					</div>
				</div>
			</div>
		);
	}
}

Payment.propTypes = {};

export default injectIntl(Payment);
